import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Grid, IconButton, TextField, Tooltip } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const chipSx = {
  height: 22,
  fontSize: 11,
  borderRadius: '12px',
  '& .MuiChip-label': {
    px: 1,
    py: 0,
  },
}

const activeChipSx = {
  ...chipSx,
  background: '#fff',
  color: '#374151',
  border: '1px solid #9ca3af',
  cursor: 'pointer',
  '&:hover': {
    background: '#f9fafb',
  },
}

const selectedChipSx = {
  ...chipSx,
  background: '#70778B',
  color: '#fff',
  border: '1px solid #70778B',
  cursor: 'pointer',
}

const infoChipSx = {
  ...chipSx,
  background: '#f3f4f6',
  color: '#4b5563',
  border: '1px solid #d1d5db',
  cursor: 'default',
}

function hasMetricValue(value) {
  return Number.isFinite(Number(value))
}

function formatMetric(value, suffix = '') {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  const rounded = Number(n.toFixed(1))
  return `${rounded}${suffix}`
}

function roundMetricValue(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Number(n.toFixed(1))
}

function normalizeNumericInput(value) {
  return String(value ?? '').replace(/,/g, '')
}

function formatControlValue(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
}

function slugifyChipPart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function AnnualControl({
  label,
  value,
  onApply,
  chips = [],
  disabled = false,
  readOnly = false,
  wrapInGridItem = true,
}) {
  const [inputValue, setInputValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (isEditing) return
    setInputValue(formatControlValue(value))
  }, [value, isEditing])

  const commit = () => {
    if (disabled || readOnly || typeof onApply !== 'function') return
    const n = Number(normalizeNumericInput(inputValue))
    if (!Number.isFinite(n)) return
    if (roundMetricValue(n) === roundMetricValue(value)) return
    onApply(n)
  }

  const content = (
    <>
      <TextField
        label={label}
        size="small"
        fullWidth
        value={inputValue}
        disabled={disabled}
        InputProps={{ readOnly }}
        onFocus={() => {
          if (disabled || readOnly) return
          setIsEditing(true)
          setInputValue(normalizeNumericInput(formatControlValue(value)))
        }}
        onChange={(e) => {
          const raw = normalizeNumericInput(e.target.value)
          if (!/^\d*(\.\d*)?$/.test(raw)) return
          setInputValue(raw)
        }}
        onBlur={() => {
          commit()
          setIsEditing(false)
          setInputValue(formatControlValue(inputValue))
        }}
        inputProps={{ inputMode: 'decimal' }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1, gap: 1 }}>
        {chips.map((chip) => {
          const hasValue = hasMetricValue(chip.value)
          const isActive = !readOnly && !chip.disabled && typeof chip.onClick === 'function'
          const isSelected = isActive && hasValue && roundMetricValue(chip.value) === roundMetricValue(value)
          const renderedLabel = !hasValue
            ? chip.label
            : `${chip.label} ${formatMetric(chip.value, chip.suffix || '')}`
          const el = (
            <Chip
              key={chip.label}
              data-testid={`annual-chip-${slugifyChipPart(label)}-${slugifyChipPart(chip.label)}`}
              data-selected={isSelected ? 'true' : 'false'}
              aria-pressed={isSelected ? 'true' : 'false'}
              label={renderedLabel}
              size="small"
              clickable={isActive}
              onClick={isActive ? chip.onClick : undefined}
              sx={{
                ...(isActive ? (isSelected ? selectedChipSx : activeChipSx) : infoChipSx),
                opacity: chip.disabled && !hasValue ? 0.9 : 1,
              }}
              variant={isActive && isSelected ? 'filled' : 'outlined'}
            />
          )
          return chip.tooltip ? (
            <Tooltip key={chip.label} title={chip.tooltip}>
              <span>{el}</span>
            </Tooltip>
          ) : el
        })}
      </Box>
    </>
  )

  if (!wrapInGridItem) return content

  return (
    <Grid item xs={12} md={6} lg={3}>
      {content}
    </Grid>
  )
}

function PlanEditMonthlyHeader({
  annualValues,
  collapsed = false,
  onCollapsedChange,
  onApplyAnnualSales,
  onApplyAnnualMarkdowns,
  onApplyAnnualTurn,
  onApplyAnnualImu,
  onBalanceAllInflows,
  onSimpleKFlow,
  readOnly = false,
}) {
  const salesChips = useMemo(() => ([
    { label: 'Linear', value: annualValues?.linearSalesModel, disabled: true, tooltip: 'Model value not yet available in the new planner.' },
    { label: 'Calv v1', value: annualValues?.calc1SalesModel, disabled: true, tooltip: 'Model value not yet available in the new planner.' },
    { label: 'Calv v3', value: annualValues?.calc3SalesModel, disabled: true, tooltip: 'Model value not yet available in the new planner.' },
    { label: 'LM Plan', value: annualValues?.salesAnnualPlanLM, disabled: true, tooltip: 'Legacy LM Plan model value is not yet available in the new planner.' },
  ]), [annualValues])

  const markdownChips = useMemo(() => ([
    {
      label: 'Actual L12',
      value: annualValues?.annualMarkdownsL12,
      suffix: '%',
      disabled: typeof onApplyAnnualMarkdowns !== 'function',
      onClick: () => onApplyAnnualMarkdowns?.(Number(annualValues?.annualMarkdownsL12 || 0)),
    },
  ]), [annualValues, onApplyAnnualMarkdowns])

  const turnChips = useMemo(() => ([
    {
      label: 'Actual',
      value: annualValues?.annualTurnActual,
      disabled: typeof onApplyAnnualTurn !== 'function',
      onClick: () => onApplyAnnualTurn?.(Number(annualValues?.annualTurnActual || 0)),
    },
    {
      label: 'Trend',
      value: annualValues?.trendedTurn,
      disabled: typeof onApplyAnnualTurn !== 'function' || !hasMetricValue(annualValues?.trendedTurn),
      onClick: () => onApplyAnnualTurn?.(Number(annualValues?.trendedTurn || 0)),
    },
    {
      label: 'LM',
      value: annualValues?.turnLm,
      disabled: true,
      tooltip: 'Legacy LM turn value is not yet available in the new planner.',
    },
  ]), [annualValues, onApplyAnnualTurn])

  const trendedTurnSourceLabel = useMemo(() => {
    if (annualValues?.trendedTurnSource === 'weekly') return 'Loaded from Weekly'
    if (annualValues?.trendedTurnSource === 'monthly') return 'Loaded from Monthly'
    return null
  }, [annualValues])

  const imuChips = useMemo(() => ([
    {
      label: 'Actual',
      value: annualValues?.imuActual,
      suffix: '%',
      disabled: typeof onApplyAnnualImu !== 'function',
      onClick: () => onApplyAnnualImu?.(Number(annualValues?.imuActual || 0)),
    },
    {
      label: 'LM',
      value: annualValues?.imuLM,
      suffix: '%',
      disabled: true,
      tooltip: 'Legacy LM IMU value is not yet available in the new planner.',
    },
  ]), [annualValues, onApplyAnnualImu])

  return (
    <Box sx={{ mb: 2, position: 'relative', pr: 6 }}>
      <Tooltip title={collapsed ? 'Expand annual header' : 'Collapse annual header'}>
        <IconButton
          size="small"
          aria-label={collapsed ? 'Expand annual header' : 'Collapse annual header'}
          onClick={() => onCollapsedChange?.(!collapsed)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 20,
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
            '&:hover': {
              backgroundColor: '#f8fafc',
            },
          }}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {!collapsed ? (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="contained" size="small" onClick={onBalanceAllInflows} disabled={readOnly || typeof onBalanceAllInflows !== 'function'} sx={{ minHeight: 28, py: 0.25 }}>Balance all Inflow</Button>
            <Button variant="contained" size="small" onClick={onSimpleKFlow} disabled={readOnly || typeof onSimpleKFlow !== 'function'} sx={{ minHeight: 28, py: 0.25 }}>Simple K Flow</Button>
          </Box>

          <Grid container spacing={2}>
            <AnnualControl
              label="Annual Sales"
              value={annualValues?.annualSalesPlan}
              onApply={onApplyAnnualSales}
              chips={salesChips}
              readOnly={readOnly}
            />
            <AnnualControl
              label="Annual Markdowns"
              value={annualValues?.annualMarkdownsPlan}
              onApply={onApplyAnnualMarkdowns}
              chips={markdownChips}
              readOnly={readOnly}
            />
            <Grid item xs={12} md={6} lg={3}>
              <Box
                data-testid="turn-metrics-row"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Box data-testid="annual-turn-container" sx={{ flex: 1, minWidth: 0 }}>
                  <AnnualControl
                    label="Annual Turn"
                    value={annualValues?.annualTurnPlan}
                    onApply={onApplyAnnualTurn}
                    chips={turnChips}
                    readOnly={readOnly}
                    wrapInGridItem={false}
                  />
                </Box>
                {hasMetricValue(annualValues?.trendedTurn) ? (
                  <Box data-testid="trended-turn-container" sx={{ flex: 1, minWidth: 0 }}>
                    <AnnualControl
                      label="Trended Turn"
                      value={annualValues?.trendedTurn}
                      readOnly
                      wrapInGridItem={false}
                    />
                    {trendedTurnSourceLabel ? (
                      <Chip
                        size="small"
                        label={trendedTurnSourceLabel}
                        color={annualValues?.trendedTurnSource === 'weekly' ? 'default' : 'info'}
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <AnnualControl
              label="IMU"
              value={annualValues?.imuPlan}
              onApply={onApplyAnnualImu}
              chips={imuChips}
              readOnly={readOnly || typeof onApplyAnnualImu !== 'function'}
            />
          </Grid>
        </>
      ) : null}
    </Box>
  )
}

export default PlanEditMonthlyHeader

